// Assistente (Claude): pedido em linguagem natural → consultas na hora e ações propostas com confirmação.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../api';
import { Button, Card, ErrorBox, inputCls } from '../components/ui';

interface Proposal {
  token: string;
  kind: string;
  summary: string;
  params: Record<string, unknown>;
  status?: 'pendente' | 'feito' | 'descartado' | 'erro';
  note?: string;
}
interface Msg {
  role: 'user' | 'assistant';
  text: string;
  proposals?: Proposal[];
}

const KEY = 'mcc-assistente';
const SUGGESTIONS = [
  'Como está a operação esta semana? O que aguarda minha aprovação?',
  'Crie uma demanda de post para a Página sobre Wi-Fi em hospitais, publicação quinta às 11h',
  'Faça um carrossel em PDF da peça ASSET-000005',
  'Remarque a ASSET-000003 para amanhã às 10h',
  'Busque tudo sobre Fortinet',
];

/** Texto com IDs clicáveis (MKT-… → demanda, ASSET-… → peça). */
function Linked({ text }: { text: string }) {
  const parts = text.split(/(MKT-\d{4}-\d{6}|ASSET-\d{6})/g);
  return (
    <>
      {parts.map((p, i) =>
        /^MKT-/.test(p) ? <Link key={i} className="font-semibold text-brand-700 underline" to={`/demandas/${p}`}>{p}</Link> : /^ASSET-/.test(p) ? <Link key={i} className="font-semibold text-brand-700 underline" to={`/conteudo/${p}`}>{p}</Link> : <span key={i}>{p}</span>,
      )}
    </>
  );
}

export function Assistant() {
  const [msgs, setMsgs] = useState<Msg[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as Msg[];
    } catch {
      return [];
    }
  });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(msgs.slice(-30)));
    } catch {
      /* armazenamento indisponível: segue sem histórico */
    }
    end.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  const send = async (prompt: string) => {
    const t = prompt.trim();
    if (!t || busy) return;
    const next: Msg[] = [...msgs, { role: 'user', text: t }];
    setMsgs(next);
    setText('');
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ reply: string; proposals: Proposal[] }>('/api/assistant', { method: 'POST', body: { messages: next.slice(-10).map((m) => ({ role: m.role, text: m.text })) } });
      setMsgs([...next, { role: 'assistant', text: r.reply, proposals: r.proposals.map((p) => ({ ...p, status: 'pendente' })) }]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Não foi possível falar com o assistente.');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (mi: number, pi: number, confirm: boolean) => {
    const update = (patch: Partial<Proposal>) => setMsgs((all) => all.map((m, i) => (i !== mi ? m : { ...m, proposals: m.proposals?.map((p, j) => (j === pi ? { ...p, ...patch } : p)) })));
    if (!confirm) return update({ status: 'descartado' });
    const p = msgs[mi].proposals![pi];
    try {
      const r = await api<{ result: any }>('/api/assistant/execute', { method: 'POST', body: { token: p.token } });
      const id = r.result?.id ?? r.result?.requestsOpened?.join(', ') ?? '';
      update({ status: 'feito', note: id ? `Feito: ${id}` : 'Feito.' });
    } catch (e) {
      update({ status: 'erro', note: e instanceof ApiError ? e.message : 'Falhou.' });
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Assistente (Claude)</h1>
        <p className="text-sm text-slate-600">
          Peça em linguagem natural. Consultas respondem na hora; mudanças viram propostas que só acontecem quando você clica em <strong>Confirmar</strong>, com as permissões do seu perfil. Aprovar e publicar continuam no painel.
        </p>
      </div>

      <Card title="Conversa">
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1" aria-live="polite">
          {!msgs.length && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Sugestões:</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} className="block w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm hover:bg-brand-50" onClick={() => setText(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}
          {msgs.map((m, mi) => (
            <div key={mi} className={m.role === 'user' ? 'ml-10 rounded-lg bg-brand-900 p-3 text-sm text-white' : 'mr-10 rounded-lg border border-slate-200 bg-white p-3 text-sm'}>
              <p className="whitespace-pre-line">{m.role === 'assistant' ? <Linked text={m.text} /> : m.text}</p>
              {m.proposals?.map((p, pi) => (
                <div key={pi} className="mt-2 rounded border border-amber-300 bg-amber-50 p-2">
                  <p className="font-medium text-slate-900">Ação proposta: {p.summary}</p>
                  {p.status === 'pendente' ? (
                    <div className="mt-2 flex gap-2">
                      <Button variant="success" onClick={() => void decide(mi, pi, true)}>
                        Confirmar
                      </Button>
                      <Button variant="secondary" onClick={() => void decide(mi, pi, false)}>
                        Descartar
                      </Button>
                    </div>
                  ) : (
                    <p className={`mt-1 text-xs ${p.status === 'erro' ? 'text-red-700' : 'text-slate-600'}`}>
                      {p.status === 'descartado' ? 'Descartada.' : p.note ? <Linked text={p.note} /> : null}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
          {busy && <p className="text-sm text-slate-500">O assistente está consultando o sistema…</p>}
          <div ref={end} />
        </div>
        <ErrorBox error={error} />
        <form
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void send(text);
          }}
        >
          <label htmlFor="prompt" className="sr-only">
            Seu pedido
          </label>
          <textarea
            id="prompt"
            className={inputCls}
            rows={3}
            placeholder="Ex.: crie uma demanda de carrossel sobre segmentação de rede industrial para sexta às 11h"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void send(text);
              }
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500">Ctrl+Enter envia · conta no teto mensal de IA</span>
            <div className="flex gap-2">
              {msgs.length > 0 && (
                <Button type="button" variant="secondary" disabled={busy} onClick={() => setMsgs([])}>
                  Nova conversa
                </Button>
              )}
              <Button type="submit" disabled={busy || text.trim().length < 2}>
                Enviar
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
